import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useChannelEventListener } from "@activecollab/activecollab-react-pusher";
import * as subtaskSchema from "~/schemas/schema.subtasks";
import {
  ADD_SUBTASK_FROM_EVENT_STREAM,
  COMPLETE_SUBTASK_SUCCESS,
  DELETE_SUBTASK_SUCCESS,
  EDIT_SUBTASK_SUCCESS,
  MOVE_SUBTASK_TO_TRASH_SUCCESS,
  OPEN_SUBTASK_SUCCESS,
  REORDER_SUBTASKS_SUCCESS,
  RESTORE_SUBTASK_FROM_TRASH_SUCCESS,
} from "~/store/action_types";
import { fetchAttachment } from "~/store/actions/actions.attachments";
import {
  availabilityRecordCreatedFromEventStream,
  availabilityRecordDeletedFromEventStream,
  availabilityRecordUpdatedFromEventStream,
} from "~/store/actions/actions.availabilityRecords";
import {
  availabilityTypeCreatedFromEventStream,
  availabilityTypeDeletedFromEventStream,
  availabilityTypeUpdatedFromEventStream,
} from "~/store/actions/actions.availabilityTypes";
import {
  addBigCommentFromEventStream,
  addCommentFromEventStream,
  updateCommentFromEventStream,
} from "~/store/actions/actions.comments";
import {
  companyCreatedFromEventStream,
  companyMovedToArchiveFromEventStream,
  companyMovedToTrashFromEventStream,
  companyRestoredFromArchiveFromEventStream,
  companyRestoredFromTrashFromEventStream,
  companyUpdatedFromEventStream,
} from "~/store/actions/actions.companies";
import {
  addDiscussionFromEventStream,
  discussionMovedToProjectFromEventStream,
  moveDiscussionToTrashFromEventStream,
  restoreDiscussionFromTrashFromEventStream,
  updateDiscussionFromEventStream,
} from "~/store/actions/actions.discussions";
import { deleteExpenseCategoryFromEventStream, fetchExpenseCategory } from "~/store/actions/actions.expenseCategories";
import {
  expenseCreatedFromEventStream,
  expenseMovedToProjectFromEventStream,
  expenseMovedToTrashFromEventStream,
  expenseRestoredFromTrashFromEventStream,
  expenseUpdatedFromEventStream,
} from "~/store/actions/actions.expenses";
import { deleteJobTypeFromEventStream, fetchJobType } from "~/store/actions/actions.jobTypes";
import {
  addLabelFromEventStream,
  deleteLabelFromEventStream,
  updateLabelFromEventStream,
} from "~/store/actions/actions.labels";
import {
  addNoteFromEventStream,
  moveNoteToTrashFromEventStream,
  noteMovedToProjectFromEventStream,
  restoreNoteFromTrashFromEventStream,
  updateNoteFromEventStream,
} from "~/store/actions/actions.notes";
import {
  membershipGrantedFromEventStream,
  membershipRevokedFromEventStream,
  projectCompletedFromEventStream,
  projectCreatedFromEventStream,
  projectMovedToTrashFromEventStream,
  projectReopenedFromEventStream,
  projectRestoredFromTrashFromEventStream,
  projectUpdatedFromEventStream,
} from "~/store/actions/actions.projects";
import {
  projectTemplateCreatedFromEventStream,
  projectTemplateMovedToTrashFromEventStream,
  projectTemplateRestoredFromTrashFromEventStream,
  projectTemplateUpdatedFromEventStream,
} from "~/store/actions/actions.projectTemplates";
import { addReactionFromEventStream, removeReactionFromEventStream } from "~/store/actions/actions.reactions";
import { deleteDataObject } from "~/store/actions/actions.realtime";
import {
  addRecurringTaskFromEventStream,
  recurringTaskMovedToTrashFromEventStream,
  recurringTaskRestoredFromTrashFromEventStream,
  updateRecurringTaskFromEventStream,
} from "~/store/actions/actions.recurringTasks";
import {
  addReminderFromEventStream,
  deleteReminderFromEventStream,
  updateReminderFromEventStream,
} from "~/store/actions/actions.reminders";
import {
  fetchStopwatches,
  stopwatchDeletedFromEventStream,
  stopwatchStartedFromEventStream,
  stopwatchUpdatedFromEventStream,
} from "~/store/actions/actions.stopwatches";
import { fetchSubtask } from "~/store/actions/actions.subtasks";
import {
  addTaskListFromEventStream,
  insertTaskListFromEventStream,
  taskListCompletedFromEventStream,
  taskListMovedToProjectFromEventStream,
  taskListMovedToTrashFromEventStream,
  taskListReopenedFromEventStream,
  taskListReorderedFromEventStream,
  taskListRestoredFromTrashFromEventStream,
  updateTaskListFromEventStream,
} from "~/store/actions/actions.taskLists";
import {
  addTaskFromEventStream,
  completeTaskFromEventStream,
  loadTask,
  reopenTaskFromEventStream,
  taskListChangedFromEventStream,
  taskMovedToProjectFromEventStream,
  taskMovedToTrashFromEventStream,
  taskReorderedFromEventStream,
  taskRestoredFromTrashFromEventStream,
  updateTaskFromEventStream,
} from "~/store/actions/actions.tasks";
import {
  createTeamFromEventStream,
  deleteTeamFromEventStream,
  updateTeamFromEventStream,
} from "~/store/actions/actions.teams";
import {
  timeRecordCreatedFromEventStream,
  timeRecordMovedToProjectFromEventStream,
  timeRecordMovedToTrashFromEventStream,
  timeRecordRestoredFromTrashFromEventStream,
  timeRecordUpdatedFromEventStream,
} from "~/store/actions/actions.timeRecords";
import {
  loadAllUsers,
  userCreatedFromEventStream,
  userMovedToArchiveFromEventStream,
  userMovedToTrashFromEventStream,
  userRestoredFromArchiveFromEventStream,
  userRestoredFromTrashFromEventStream,
  userUpdatedFromEventStream,
} from "~/store/actions/actions.users";
import { getLoggedUserId, makeIsLoggedUserOwnerSelector } from "~/store/selectors/selector.users";

export const UserRealTimeListener = () => {
  const dispatch = useDispatch();

  const getIsLogedUserOwner = useMemo(makeIsLoggedUserOwnerSelector, []);
  const loggedUserId = useSelector(getLoggedUserId);
  const isLoggedUserOwner = useSelector(getIsLogedUserOwner);

  const onAddComment = useCallback(
    (data) => {
      dispatch(addCommentFromEventStream(data));
    },
    [dispatch]
  );

  const onAddBigComment = useCallback(
    (data) => {
      dispatch(addBigCommentFromEventStream(data));
      if (typeof data?.parent_type === "string" && data?.parent_type?.toLowerCase() === "task") {
        dispatch(loadTask(data.project_id, data.parent_id, true));
      }
    },
    [dispatch]
  );

  const onUpdateComment = useCallback(
    (data) => {
      dispatch(updateCommentFromEventStream(data));
    },
    [dispatch]
  );

  const onCommentCreated = useCallback(
    (data) => {
      data.is_complete_data ? onAddComment(data) : onAddBigComment(data);
    },
    [onAddBigComment, onAddComment]
  );

  const onCommentUpdated = useCallback(
    (data) => {
      data.is_complete_data ? onUpdateComment(data) : onAddBigComment(data);
    },
    [onAddBigComment, onUpdateComment]
  );

  /**
   * Comments
   */
  useChannelEventListener("CommentCreated", onCommentCreated);
  useChannelEventListener("CommentMovedToTrash", onCommentUpdated);
  useChannelEventListener("CommentUpdated", onCommentUpdated);
  useChannelEventListener("CommentRestoredFromTrash", onCommentUpdated);

  const onReactionCreated = useCallback(
    (data) => {
      dispatch(addReactionFromEventStream(data));
    },
    [dispatch]
  );

  const onReactionDeleted = useCallback(
    (data) => {
      dispatch(removeReactionFromEventStream(data));
    },
    [dispatch]
  );

  /**
   * Reactions
   */
  useChannelEventListener("ReactionCreated", onReactionCreated);
  useChannelEventListener("ReactionDeleted", onReactionDeleted);

  const onTaskCreated = useCallback(
    (data) => {
      dispatch(addTaskFromEventStream(data));
    },
    [dispatch]
  );

  const onTaskUpdated = useCallback(
    (data) => {
      dispatch(updateTaskFromEventStream(data));
    },
    [dispatch]
  );

  const onTaskCompleted = useCallback(
    (data) => {
      dispatch(completeTaskFromEventStream(data));
    },
    [dispatch]
  );

  const onTaskReopened = useCallback(
    (data) => {
      dispatch(reopenTaskFromEventStream(data));
    },
    [dispatch]
  );

  const onTaskMovedToTrash = useCallback(
    (data) => {
      dispatch(taskMovedToTrashFromEventStream(data));
    },
    [dispatch]
  );

  const onTaskRestoredFromTrash = useCallback(
    (data) => {
      dispatch(taskRestoredFromTrashFromEventStream(data));
    },
    [dispatch]
  );

  const onTaskReordered = useCallback(
    (data) => {
      dispatch(taskReorderedFromEventStream(data));
    },
    [dispatch]
  );

  const onTaskMovedToProject = useCallback(
    (data) => {
      dispatch(taskMovedToProjectFromEventStream(data));
    },
    [dispatch]
  );

  const onTrackingContextUpdated = useCallback(
    ({ id, project_id, type }) => {
      if (type === "Task") {
        dispatch(loadTask(project_id, id, true));
      }
    },
    [dispatch]
  );

  /**
   * Tasks
   */
  useChannelEventListener("TaskUpdated", onTaskUpdated);
  useChannelEventListener("TaskCreated", onTaskCreated);
  useChannelEventListener("TaskCompleted", onTaskCompleted);
  useChannelEventListener("TaskReopened", onTaskReopened);
  useChannelEventListener("TaskMovedToTrash", onTaskMovedToTrash);
  useChannelEventListener("TaskRestoredFromTrash", onTaskRestoredFromTrash);
  useChannelEventListener("TaskReordered", onTaskReordered);
  useChannelEventListener("TaskMovedToProject", onTaskMovedToProject);
  useChannelEventListener("TrackingContextUpdated", onTrackingContextUpdated);

  const onTaskListChanged = useCallback(
    (data) => {
      dispatch(taskListChangedFromEventStream(data));
    },
    [dispatch]
  );

  const onTaskListCreated = useCallback(
    (data) => {
      dispatch(addTaskListFromEventStream(data));
    },
    [dispatch]
  );

  const onTaskListInserted = useCallback(
    (data) => {
      dispatch(insertTaskListFromEventStream(data));
    },
    [dispatch]
  );

  const onTaskListUpdated = useCallback(
    (data) => {
      dispatch(updateTaskListFromEventStream(data));
    },
    [dispatch]
  );

  const onTaskListReordered = useCallback(
    (data) => {
      dispatch(taskListReorderedFromEventStream(data));
    },
    [dispatch]
  );

  const onTaskListMovedToTrash = useCallback(
    (data) => {
      dispatch(taskListMovedToTrashFromEventStream(data));
    },
    [dispatch]
  );

  const onTaskListRestoredFromTrash = useCallback(
    (data) => {
      dispatch(taskListRestoredFromTrashFromEventStream(data));
    },
    [dispatch]
  );

  const onTaskListCompleted = useCallback(
    (data) => {
      dispatch(taskListCompletedFromEventStream(data));
    },
    [dispatch]
  );

  const onTaskListReopened = useCallback(
    (data) => {
      dispatch(taskListReopenedFromEventStream(data));
    },
    [dispatch]
  );

  const onTaskListMovedToProject = useCallback(
    (data) => {
      dispatch(taskListMovedToProjectFromEventStream(data));
    },
    [dispatch]
  );

  /**
   * Task Lists
   */
  useChannelEventListener("TaskListChanged", onTaskListChanged);
  useChannelEventListener("TaskListCreated", onTaskListCreated);
  useChannelEventListener("TaskListInserted", onTaskListInserted);
  useChannelEventListener("TaskListUpdated", onTaskListUpdated);
  useChannelEventListener("TaskListReordered", onTaskListReordered);
  useChannelEventListener("TaskListMovedToTrash", onTaskListMovedToTrash);
  useChannelEventListener("TaskListRestoredFromTrash", onTaskListRestoredFromTrash);
  useChannelEventListener("TaskListCompleted", onTaskListCompleted);
  useChannelEventListener("TaskListReopened", onTaskListReopened);
  useChannelEventListener("TaskListMovedToProject", onTaskListMovedToProject);

  const onStopwatchCreated = useCallback(
    (data) => {
      dispatch(stopwatchStartedFromEventStream(data));
    },
    [dispatch]
  );

  const onStopwatchUpdated = useCallback(
    (data) => {
      dispatch(stopwatchUpdatedFromEventStream(data));
    },
    [dispatch]
  );

  const onStopwatchDeleted = useCallback(
    (data) => {
      dispatch(stopwatchDeletedFromEventStream(data));
    },
    [dispatch]
  );

  /**
   * Stopwatches
   */
  useChannelEventListener("StopwatchCreated", onStopwatchCreated);
  useChannelEventListener("StopwatchUpdated", onStopwatchUpdated);
  useChannelEventListener("StopwatchDeleted", onStopwatchDeleted);

  const onTimeRecordCreated = useCallback(
    (data) => {
      dispatch(timeRecordCreatedFromEventStream(data));
    },
    [dispatch]
  );

  const onTimeRecordUpdated = useCallback(
    (data) => {
      dispatch(timeRecordUpdatedFromEventStream(data));
    },
    [dispatch]
  );

  const onTimeRecordMovedToTrash = useCallback(
    (data) => {
      dispatch(timeRecordMovedToTrashFromEventStream(data));
    },
    [dispatch]
  );

  const onTimeRecordRestoredFromTrash = useCallback(
    (data) => {
      dispatch(timeRecordRestoredFromTrashFromEventStream(data));
    },
    [dispatch]
  );

  const onTimeRecordMovedToProject = useCallback(
    (data) => {
      dispatch(timeRecordMovedToProjectFromEventStream(data));
    },
    [dispatch]
  );

  const onTimeRecordRemovedFromProject = useCallback(
    (data) => {
      if (data.parent_type === "Task") {
        dispatch(loadTask(data.project_id, data.parent_id, true));
      }
    },
    [dispatch]
  );

  /**
   * TimeRecords
   */
  useChannelEventListener("TimeRecordCreated", onTimeRecordCreated);
  useChannelEventListener("TimeRecordUpdated", onTimeRecordUpdated);
  useChannelEventListener("TimeRecordMovedToTrash", onTimeRecordMovedToTrash);
  useChannelEventListener("TimeRecordRestoredFromTrash", onTimeRecordRestoredFromTrash);
  useChannelEventListener("TimeRecordMovedToProject", onTimeRecordMovedToProject);
  useChannelEventListener("TimeRecordRemovedFromProject", onTimeRecordRemovedFromProject);

  const onExpenseCreated = useCallback(
    (data) => {
      dispatch(expenseCreatedFromEventStream(data));
    },
    [dispatch]
  );

  const onExpenseUpdated = useCallback(
    (data) => {
      dispatch(expenseUpdatedFromEventStream(data));
    },
    [dispatch]
  );

  const onExpenseMovedToTrash = useCallback(
    (data) => {
      dispatch(expenseMovedToTrashFromEventStream(data));
    },
    [dispatch]
  );

  const onExpenseRestoredFromTrash = useCallback(
    (data) => {
      dispatch(expenseRestoredFromTrashFromEventStream(data));
    },
    [dispatch]
  );

  const onExpenseMovedToProject = useCallback(
    (data) => {
      dispatch(expenseMovedToProjectFromEventStream(data));
    },
    [dispatch]
  );

  const onExpenseRemovedFromProject = useCallback(
    (data) => {
      if (data.parent_type === "Task") {
        dispatch(loadTask(data.project_id, data.parent_id, true));
      }
    },
    [dispatch]
  );

  /**
   * Expenses
   */
  useChannelEventListener("ExpenseCreated", onExpenseCreated);
  useChannelEventListener("ExpenseUpdated", onExpenseUpdated);
  useChannelEventListener("ExpenseMovedToTrash", onExpenseMovedToTrash);
  useChannelEventListener("ExpenseRestoredFromTrash", onExpenseRestoredFromTrash);
  useChannelEventListener("ExpenseMovedToProject", onExpenseMovedToProject);
  useChannelEventListener("ExpenseRemovedFromProject", onExpenseRemovedFromProject);

  const onAvailabilityRecordCreated = useCallback(
    (data) => {
      dispatch(availabilityRecordCreatedFromEventStream(data));
    },
    [dispatch]
  );

  const onAvailabilityRecordUpdated = useCallback(
    (data) => {
      dispatch(availabilityRecordUpdatedFromEventStream(data));
    },
    [dispatch]
  );

  const onAvailabilityRecordDeleted = useCallback(
    (data) => {
      dispatch(availabilityRecordDeletedFromEventStream(data));
    },
    [dispatch]
  );

  /**
   * AvailabilityRecords
   */
  useChannelEventListener("AvailabilityRecordCreated", onAvailabilityRecordCreated);
  useChannelEventListener("AvailabilityRecordUpdated", onAvailabilityRecordUpdated);
  useChannelEventListener("AvailabilityRecordDeleted", onAvailabilityRecordDeleted);

  const onAvailabilityTypeCreated = useCallback(
    (data) => {
      dispatch(availabilityTypeCreatedFromEventStream(data));
    },
    [dispatch]
  );

  const onAvailabilityTypeUpdated = useCallback(
    (data) => {
      dispatch(availabilityTypeUpdatedFromEventStream(data));
    },
    [dispatch]
  );

  const onAvailabilityTypeDeleted = useCallback(
    (data) => {
      dispatch(availabilityTypeDeletedFromEventStream(data));
    },
    [dispatch]
  );

  /**
   * AvailabilityTypes
   */
  useChannelEventListener("AvailabilityTypeCreated", onAvailabilityTypeCreated);
  useChannelEventListener("AvailabilityTypeUpdated", onAvailabilityTypeUpdated);
  useChannelEventListener("AvailabilityTypeDeleted", onAvailabilityTypeDeleted);

  const onProjectCreated = useCallback(
    (data) => {
      dispatch(projectCreatedFromEventStream(data));
    },
    [dispatch]
  );

  const onProjectUpdated = useCallback(
    (data) => {
      dispatch(projectUpdatedFromEventStream(data));
    },
    [dispatch]
  );

  const onProjectCompleted = useCallback(
    (data) => {
      dispatch(projectCompletedFromEventStream(data));
    },
    [dispatch]
  );

  const onProjectReopened = useCallback(
    (data) => {
      dispatch(projectReopenedFromEventStream(data));
    },
    [dispatch]
  );

  const onProjectMovedToTrash = useCallback(
    (data) => {
      dispatch(projectMovedToTrashFromEventStream(data));
    },
    [dispatch]
  );

  const onProjectRestoredFromTrash = useCallback(
    (data) => {
      dispatch(projectRestoredFromTrashFromEventStream(data));
    },
    [dispatch]
  );

  /**
   * Projects
   */
  useChannelEventListener("ProjectCreated", onProjectCreated);
  useChannelEventListener("ProjectUpdated", onProjectUpdated);
  useChannelEventListener("ProjectCompleted", onProjectCompleted);
  useChannelEventListener("ProjectReopened", onProjectReopened);
  useChannelEventListener("ProjectMovedToTrash", onProjectMovedToTrash);
  useChannelEventListener("ProjectRestoredFromTrash", onProjectRestoredFromTrash);

  const onProjectTemplateCreated = useCallback(
    (data) => {
      dispatch(projectTemplateCreatedFromEventStream(data));
    },
    [dispatch]
  );

  const onProjectTemplateUpdated = useCallback(
    (data) => {
      dispatch(projectTemplateUpdatedFromEventStream(data));
    },
    [dispatch]
  );

  const onProjectTemplateMovedToTrash = useCallback(
    (data) => {
      dispatch(projectTemplateMovedToTrashFromEventStream(data));
    },
    [dispatch]
  );

  const onProjectTemplateRestoredFromTrash = useCallback(
    (data) => {
      dispatch(projectTemplateRestoredFromTrashFromEventStream(data));
    },
    [dispatch]
  );

  /**
   * Project Templates
   */
  useChannelEventListener("ProjectTemplateCreated", onProjectTemplateCreated);
  useChannelEventListener("ProjectTemplateUpdated", onProjectTemplateUpdated);
  useChannelEventListener("ProjectTemplateMovedToTrash", onProjectTemplateMovedToTrash);
  useChannelEventListener("ProjectTemplateRestoredFromTrash", onProjectTemplateRestoredFromTrash);

  const onUserCreated = useCallback(
    (data) => {
      dispatch(userCreatedFromEventStream(data));
    },
    [dispatch]
  );

  const onUserUpdated = useCallback(
    (data) => {
      dispatch(userUpdatedFromEventStream(data));
    },
    [dispatch]
  );

  const onUserMovedToTrash = useCallback(
    (data) => {
      dispatch(userMovedToTrashFromEventStream(data));
    },
    [dispatch]
  );

  const onUserRestoredFromTrash = useCallback(
    (data) => {
      dispatch(userRestoredFromTrashFromEventStream(data));
    },
    [dispatch]
  );

  const onUserMovedToArchive = useCallback(
    (data) => {
      dispatch(userMovedToArchiveFromEventStream(data));
    },
    [dispatch]
  );

  const onUserRestoredFromArchive = useCallback(
    (data) => {
      dispatch(userRestoredFromArchiveFromEventStream(data));
    },
    [dispatch]
  );

  /**
   * Users
   */
  useChannelEventListener("OwnerCreated", onUserCreated);
  useChannelEventListener("MemberCreated", onUserCreated);
  useChannelEventListener("ClientCreated", onUserCreated);
  useChannelEventListener("UserUpdated", onUserUpdated);
  useChannelEventListener("UserMovedToTrash", onUserMovedToTrash);
  useChannelEventListener("UserRestoredFromTrash", onUserRestoredFromTrash);
  useChannelEventListener("UserMovedToArchive", onUserMovedToArchive);
  useChannelEventListener("UserRestoredFromArchive", onUserRestoredFromArchive);

  /**
   * Companies
   */

  const onCompanyCreated = useCallback(
    (data) => {
      dispatch(companyCreatedFromEventStream(data));
    },
    [dispatch]
  );

  const onCompanyUpdated = useCallback(
    (data) => {
      dispatch(companyUpdatedFromEventStream(data));
    },
    [dispatch]
  );

  const onCompanyMovedToTrash = useCallback(
    (data) => {
      dispatch(companyMovedToTrashFromEventStream(data));
    },
    [dispatch]
  );

  const onCompanyRestoredFromTrash = useCallback(
    (data) => {
      dispatch(companyRestoredFromTrashFromEventStream(data));
    },
    [dispatch]
  );

  const onCompanyMovedToArchive = useCallback(
    (data) => {
      dispatch(companyMovedToArchiveFromEventStream(data));
      if (data.members.length > 0) {
        dispatch(loadAllUsers());
      }
    },
    [dispatch]
  );

  const onCompanyRestoredFromArchive = useCallback(
    (data) => {
      dispatch(companyRestoredFromArchiveFromEventStream(data));
      if (data.members.length > 0) {
        dispatch(loadAllUsers());
      }
    },
    [dispatch]
  );

  useChannelEventListener("CompanyCreated", onCompanyCreated);
  useChannelEventListener("CompanyUpdated", onCompanyUpdated);
  useChannelEventListener("CompanyMovedToTrash", onCompanyMovedToTrash);
  useChannelEventListener("CompanyRestoredFromTrash", onCompanyRestoredFromTrash);
  useChannelEventListener("CompanyMovedToArchive", onCompanyMovedToArchive);
  useChannelEventListener("CompanyRestoredFromArchive", onCompanyRestoredFromArchive);

  const onMembershipGranted = useCallback(
    (data) => {
      dispatch(membershipGrantedFromEventStream(data));
      dispatch(fetchStopwatches());
    },
    [dispatch]
  );

  const onMembershipRevoked = useCallback(
    (data) => {
      dispatch(membershipRevokedFromEventStream(data, loggedUserId, isLoggedUserOwner));
    },
    [dispatch, isLoggedUserOwner, loggedUserId]
  );

  /**
   * Permissions
   */
  useChannelEventListener("ProjectMembershipGranted", onMembershipGranted);
  useChannelEventListener("ProjectMembershipRevoked", onMembershipRevoked);

  const onSubtaskRealtimeEvent = useCallback(
    (data, action) => {
      if (data && data.is_complete_data) {
        dispatch(action);
      } else {
        const { project_id, task_id, id } = data;
        dispatch(fetchSubtask(project_id, task_id, id));
      }
    },
    [dispatch]
  );

  const onSubtaskUpated = useCallback(
    (data) => {
      const action = {
        type: EDIT_SUBTASK_SUCCESS,
        id: data.id,
        response: { single: data },
        schema: { single: subtaskSchema.subtask },
      };
      onSubtaskRealtimeEvent(data, action);
    },
    [onSubtaskRealtimeEvent]
  );

  const onSubtaskCreated = useCallback(
    (data) => {
      const action = {
        type: ADD_SUBTASK_FROM_EVENT_STREAM,
        taskId: data.task_id,
        response: { single: data },
        schema: { single: subtaskSchema.subtask },
      };
      onSubtaskRealtimeEvent(data, action);
    },
    [onSubtaskRealtimeEvent]
  );

  const onSubtaskCompleted = useCallback(
    (data) => {
      const action = {
        type: COMPLETE_SUBTASK_SUCCESS,
        id: data.id,
        response: { single: data },
        schema: { single: subtaskSchema.subtask },
      };
      onSubtaskRealtimeEvent(data, action);
    },
    [onSubtaskRealtimeEvent]
  );

  const onSubtaskReopened = useCallback(
    (data) => {
      const action = {
        type: OPEN_SUBTASK_SUCCESS,
        id: data.id,
        response: { single: data },
        schema: { single: subtaskSchema.subtask },
      };
      onSubtaskRealtimeEvent(data, action);
    },
    [onSubtaskRealtimeEvent]
  );

  const onSubtaskMovedToTrash = useCallback(
    (data) => {
      const action = {
        type: MOVE_SUBTASK_TO_TRASH_SUCCESS,
        objectType: `Subtask`,
        id: data.id,
        response: { single: data },
        schema: { single: subtaskSchema.subtask },
      };
      onSubtaskRealtimeEvent(data, action);
    },
    [onSubtaskRealtimeEvent]
  );

  const onSubtaskRestoredFromTrash = useCallback(
    (data) => {
      const action = {
        type: RESTORE_SUBTASK_FROM_TRASH_SUCCESS,
        objectType: `Subtask`,
        id: data.id,
        response: { single: data },
        schema: { single: subtaskSchema.subtask },
      };
      onSubtaskRealtimeEvent(data, action);
    },
    [onSubtaskRealtimeEvent]
  );

  const onSubtaskReordered = useCallback(
    (data) => {
      const action = {
        type: REORDER_SUBTASKS_SUCCESS,
        taskId: data.task_id,
        response: data.ordered_subtask_ids || [],
      };
      onSubtaskRealtimeEvent(data, action);
    },
    [onSubtaskRealtimeEvent]
  );

  const onSubtaskDeleted = useCallback(
    (data) => {
      const action = {
        type: DELETE_SUBTASK_SUCCESS,
        projectId: data.project_id,
        taskId: data.task_id,
        id: data.id,
      };
      onSubtaskRealtimeEvent(data, action);
    },
    [onSubtaskRealtimeEvent]
  );

  /**
   * Subtasks
   */
  useChannelEventListener("SubtaskUpdated", onSubtaskUpated);
  useChannelEventListener("SubtaskCreated", onSubtaskCreated);
  useChannelEventListener("SubtaskCompleted", onSubtaskCompleted);
  useChannelEventListener("SubtaskReopened", onSubtaskReopened);
  useChannelEventListener("SubtaskMovedToTrash", onSubtaskMovedToTrash);
  useChannelEventListener("SubtaskRestoredFromTrash", onSubtaskRestoredFromTrash);
  useChannelEventListener("SubtaskReordered", onSubtaskReordered);
  useChannelEventListener("SubtaskDeleted", onSubtaskDeleted);

  /**
   * NotificationRecipients
   */

  const onNoteCreated = useCallback(
    (data) => {
      dispatch(addNoteFromEventStream(data));
    },
    [dispatch]
  );

  const onNoteUpdated = useCallback(
    (data) => {
      dispatch(updateNoteFromEventStream(data));
    },
    [dispatch]
  );
  const onNoteMovedToTrash = useCallback(
    (data) => {
      dispatch(moveNoteToTrashFromEventStream(data));
    },
    [dispatch]
  );

  const onNoteRestoredFromTrash = useCallback(
    (data) => {
      dispatch(restoreNoteFromTrashFromEventStream(data));
    },
    [dispatch]
  );

  const onNoteMovedToProject = useCallback(
    (data) => {
      dispatch(noteMovedToProjectFromEventStream(data));
    },
    [dispatch]
  );

  /**
   * Notes
   */
  useChannelEventListener("NoteCreated", onNoteCreated);
  useChannelEventListener("NoteUpdated", onNoteUpdated);
  useChannelEventListener("NoteMovedToTrash", onNoteMovedToTrash);
  useChannelEventListener("NoteRestoredFromTrash", onNoteRestoredFromTrash);
  useChannelEventListener("NoteMovedToProject", onNoteMovedToProject);

  const onDiscussionCreated = useCallback(
    (data) => {
      dispatch(addDiscussionFromEventStream(data));
    },
    [dispatch]
  );

  const onDiscussionUpdated = useCallback(
    (data) => {
      dispatch(updateDiscussionFromEventStream(data));
    },
    [dispatch]
  );

  const onDiscussionMovedToTrash = useCallback(
    (data) => {
      dispatch(moveDiscussionToTrashFromEventStream(data));
    },
    [dispatch]
  );

  const onDiscussionRestoredFromTrash = useCallback(
    (data) => {
      dispatch(restoreDiscussionFromTrashFromEventStream(data));
    },
    [dispatch]
  );

  const onDiscussionMovedToProject = useCallback(
    (data) => {
      dispatch(discussionMovedToProjectFromEventStream(data));
    },
    [dispatch]
  );

  /**
   * Discussions
   */
  useChannelEventListener("DiscussionCreated", onDiscussionCreated);
  useChannelEventListener("DiscussionUpdated", onDiscussionUpdated);
  useChannelEventListener("DiscussionMovedToTrash", onDiscussionMovedToTrash);
  useChannelEventListener("DiscussionRestoredFromTrash", onDiscussionRestoredFromTrash);
  useChannelEventListener("DiscussionMovedToProject", onDiscussionMovedToProject);

  const onReminderCreated = useCallback(
    (data) => {
      dispatch(addReminderFromEventStream(data));
    },
    [dispatch]
  );

  const onReminderDeleted = useCallback(
    (data) => {
      dispatch(deleteReminderFromEventStream(data));
    },
    [dispatch]
  );

  const onReminderUpdated = useCallback(
    (data) => {
      dispatch(updateReminderFromEventStream(data));
    },
    [dispatch]
  );

  /**
   * Reminders
   */
  useChannelEventListener("ReminderCreated", onReminderCreated);
  useChannelEventListener("ReminderDeleted", onReminderDeleted);
  useChannelEventListener("ReminderUpdated", onReminderUpdated);

  const onTeamCreated = useCallback(
    (data) => {
      dispatch(createTeamFromEventStream(data));
    },
    [dispatch]
  );
  const onTeamUpdated = useCallback(
    (data) => {
      dispatch(updateTeamFromEventStream(data));
    },
    [dispatch]
  );
  const onTeamDeleted = useCallback(
    (data) => {
      const { id = 0 } = data;
      id > 0 && dispatch(deleteTeamFromEventStream(id));
    },
    [dispatch]
  );
  /**
   * Teams
   */
  useChannelEventListener("TeamCreated", onTeamCreated);
  useChannelEventListener("TeamUpdated", onTeamUpdated);
  useChannelEventListener("TeamDeleted", onTeamDeleted);

  const onDataObjectDeleted = useCallback(
    ({ id = 0, type = null }) => {
      id > 0 && type && dispatch(deleteDataObject(id, type));
    },
    [dispatch]
  );
  /**
   * Deleted object
   */
  useChannelEventListener("DataObjectDeleted", onDataObjectDeleted);

  const attachmentListener = useCallback(
    ({ id = 0 }) => {
      id > 0 && dispatch(fetchAttachment(id));
    },
    [dispatch]
  );
  /**
   * Attachment
   */
  useChannelEventListener("AttachmentCreated", attachmentListener);
  useChannelEventListener("AttachmentUpdated", attachmentListener);

  const onLabelCreated = useCallback(
    (data) => {
      dispatch(addLabelFromEventStream(data));
    },
    [dispatch]
  );

  const onLabelUpdated = useCallback(
    (data) => {
      dispatch(updateLabelFromEventStream(data));
    },
    [dispatch]
  );

  const onLabelDeleted = useCallback(
    (data) => {
      dispatch(deleteLabelFromEventStream(data));
    },
    [dispatch]
  );

  /**
   * Labels
   */
  useChannelEventListener("LabelCreated", onLabelCreated);
  useChannelEventListener("LabelUpdated", onLabelUpdated);
  useChannelEventListener("LabelDeleted", onLabelDeleted);

  const onRecurringTaskCreated = useCallback(
    (data) => {
      dispatch(addRecurringTaskFromEventStream(data));
    },
    [dispatch]
  );

  const onRecurringTaskMovedToTrash = useCallback(
    (data) => {
      dispatch(recurringTaskMovedToTrashFromEventStream(data));
    },
    [dispatch]
  );

  const onRecurringTaskUpdated = useCallback(
    (data) => {
      dispatch(updateRecurringTaskFromEventStream(data));
    },
    [dispatch]
  );

  const onRecurringTaskRestoredFromTrash = useCallback(
    (data) => {
      dispatch(recurringTaskRestoredFromTrashFromEventStream(data));
    },
    [dispatch]
  );

  /**
   * Recurring Tasks
   */
  useChannelEventListener("RecurringTaskCreated", onRecurringTaskCreated);
  useChannelEventListener("RecurringTaskMovedToTrash", onRecurringTaskMovedToTrash);
  useChannelEventListener("RecurringTaskUpdated", onRecurringTaskUpdated);
  useChannelEventListener("RecurringTaskRestoredFromTrash", onRecurringTaskRestoredFromTrash);

  /**
   * Job Types
   */
  const jobTypeListener = useCallback(
    ({ id = 0 }) => {
      dispatch(fetchJobType(id));
    },
    [dispatch]
  );

  const deleteJobTypeListener = useCallback(
    ({ id = 0 }) => {
      dispatch(deleteJobTypeFromEventStream(id));
    },
    [dispatch]
  );

  useChannelEventListener("JobTypeCreated", jobTypeListener);
  useChannelEventListener("JobTypeUpdated", jobTypeListener);
  useChannelEventListener("JobTypeMovedToArchive", jobTypeListener);
  useChannelEventListener("JobTypeDeleted", deleteJobTypeListener);

  /**
   * Expense Categories
   */
  const expenseCategoryListener = useCallback(
    ({ id = 0 }) => {
      dispatch(fetchExpenseCategory(id));
    },
    [dispatch]
  );

  const deleteExpenseCategoryListener = useCallback(
    ({ id = 0 }) => {
      dispatch(deleteExpenseCategoryFromEventStream(id));
    },
    [dispatch]
  );

  useChannelEventListener("ExpenseCategoryCreated", expenseCategoryListener);
  useChannelEventListener("ExpenseCategoryUpdated", expenseCategoryListener);
  useChannelEventListener("ExpenseCategoryMovedToArchive", expenseCategoryListener);
  useChannelEventListener("ExpenseCategoryDeleted", deleteExpenseCategoryListener);

  return null;
};
